import Dexie, { type Table } from 'dexie';
import type { ChatMessage, SessionKind } from '@domain/chat/types';
import type { FileTransferLifecycleSnapshot } from '@domain/file/api';

export interface DBMessage extends Omit<ChatMessage, 'localId'> {
  id?: number;
  localId: string;
  msgType?: number;
  msgData?: Uint8Array;
  displayType?: number;
  loadStatus?: number;
}

export interface DBSession {
  id?: number;
  sessionKey: string;
  sessionId: number;
  sessionType: SessionKind;
  lastMsg?: string;
  updatedTime: number;
  unreadCnt: number;
  latestMsgId: number;
}

export interface DBUser {
  id: number;
  name: string;
  avatar?: string;
  email?: string;
  phone?: string;
  signInfo?: string;
}

export interface DBGroup {
  id: number;
  name: string;
  avatar?: string;
  version?: number;
  shieldStatus?: number;
  memberIds?: number[];
}

export interface DBUserIdentity {
  subjectUserId: number;
  displayName: string | null;
  avatarAssetId: string | null;
  profileVersion: number;
  availability: 'ok' | 'unavailable';
  cachedAtMs: number;
}

export interface DBIdentitySyncState {
  ownerUserId: number;
  lastAppliedChangeCursor: number;
  updatedAtMs: number;
}

export interface DBFileTransferLifecycle extends FileTransferLifecycleSnapshot {
  updatedAtMs: number;
}

export interface DBFileTransferSyncState {
  ownerUserId: number;
  cursor: number;
  updatedAtMs: number;
}

export class LocsDatabase extends Dexie {
  users!: Table<DBUser, number>;
  userIdentityCache!: Table<DBUserIdentity, number>;
  identitySyncState!: Table<DBIdentitySyncState, number>;
  groups!: Table<DBGroup, number>;
  sessions!: Table<DBSession, number>;
  messages!: Table<DBMessage, number>;
  fileTransferLifecycles!: Table<DBFileTransferLifecycle, string>;
  fileTransferSyncState!: Table<DBFileTransferSyncState, number>;

  constructor(ownerUserId: number) {
    if (!Number.isSafeInteger(ownerUserId) || ownerUserId <= 0) {
      throw new Error('A positive ownerUserId is required for local database isolation');
    }

    // The complete cache is account-scoped, not only the identity table.
    // Otherwise a second account in the same browser could observe the first
    // account's sessions/messages even if public identities were isolated.
    super(`LocsDatabase:${ownerUserId}`);

    this.version(1).stores({
      users: 'id, name',
      userIdentityCache: 'subjectUserId, profileVersion',
      identitySyncState: 'ownerUserId',
      groups: 'id, name',
      sessions: '++id, sessionKey, sessionId, sessionType, updatedTime',
      messages: '++id, msgId, sessionKey, localId, ts, [sessionKey+msgId], [sessionKey+ts]'
    });
    this.version(2).stores({
      users: 'id, name',
      userIdentityCache: 'subjectUserId, profileVersion',
      identitySyncState: 'ownerUserId',
      groups: 'id, name',
      sessions: '++id, sessionKey, sessionId, sessionType, updatedTime',
      messages: '++id, msgId, sessionKey, localId, ts, [sessionKey+msgId], [sessionKey+ts]',
      fileTransferLifecycles: 'transferId, state, version, updatedAtMs',
      fileTransferSyncState: 'ownerUserId',
    });
  }

  async clearAll() {
    await this.users.clear();
    await this.groups.clear();
    await this.sessions.clear();
    await this.messages.clear();
    await this.fileTransferLifecycles.clear();
  }
  
  async getMessagesBySession(sessionKey: string, limit: number = 50, beforeTime?: number): Promise<DBMessage[]> {
    let query = this.messages.where('sessionKey').equals(sessionKey);
    if (beforeTime) {
      query = query.and((msg: DBMessage) => msg.ts < beforeTime);
    }
    const messages = await query.reverse().sortBy('ts');
    return messages.slice(0, limit);
  }
  
  async insertOrIgnoreMessage(message: DBMessage): Promise<{ id: number; inserted: boolean }> {
    return this.transaction('rw', this.messages, async () => {
      if (message.msgId === 0) {
        const id = await this.messages.add(message);
        return { id, inserted: true };
      }
      const existing = await this.messages
        .where('[sessionKey+msgId]')
        .equals([message.sessionKey, message.msgId])
        .toArray();

      const duplicate = existing.find((candidate) => {
        if (message.sessionType !== 'group') {
          return true;
        }

        return candidate.from === message.from
          && candidate.msgType === message.msgType
          && candidate.ts === message.ts
          && candidate.text === message.text;
      });

      if (duplicate) {
        return { id: duplicate.id!, inserted: false };
      }
      const id = await this.messages.add(message);
      return { id, inserted: true };
    });
  }
  
  async updateMessageMsgId(localId: string, newMsgId: number): Promise<void> {
    const message = await this.messages.where('localId').equals(localId).first();
    if (message && message.id) {
      await this.messages.update(message.id, { msgId: newMsgId, status: 'sent' });
    }
  }

  async updateMessageStatus(localId: string, status: ChatMessage['status'], msgId?: number): Promise<void> {
    const message = await this.messages.where('localId').equals(localId).first();
    if (message && message.id) {
      await this.messages.update(message.id, {
        status,
        ...(msgId == null ? {} : { msgId }),
      });
    }
  }

  async getMsgIdsInRange(sessionKey: string, msgIdBeg: number, msgIdEnd: number): Promise<number[]> {
    const msgs = await this.messages
      .where('[sessionKey+msgId]')
      .between([sessionKey, msgIdBeg], [sessionKey, msgIdEnd], true, true)
      .toArray();
    return msgs.map(m => m.msgId);
  }
  
  async findMessageByLocalId(localId: string): Promise<DBMessage | undefined> {
    return await this.messages.where('localId').equals(localId).first();
  }
  
  async findMessageByMsgId(sessionKey: string, msgId: number): Promise<DBMessage | undefined> {
    return await this.messages.where('[sessionKey+msgId]').equals([sessionKey, msgId]).first();
  }
  
  async getSessionLatestMsgId(sessionKey: string): Promise<number> {
    const messages = await this.messages
      .where('sessionKey').equals(sessionKey)
      .and((msg: DBMessage) => msg.msgId > 0)
      .reverse().sortBy('msgId');
    return messages.length > 0 ? messages[0].msgId : 0;
  }
  
  async clearSessionMessages(sessionKey: string): Promise<void> {
    const messages = await this.messages.where('sessionKey').equals(sessionKey).toArray();
    const ids = messages.map((msg: DBMessage) => msg.id!).filter(id => id != null);
    await this.messages.bulkDelete(ids as number[]);
  }

  async deleteSession(sessionKey: string): Promise<void> {
    const session = await this.sessions.where('sessionKey').equals(sessionKey).first();
    if (session?.id != null) {
      await this.sessions.delete(session.id);
    }
  }
  
  async bulkInsertMessages(messages: DBMessage[]): Promise<number> {
    let insertedCount = 0;
    for (const msg of messages) {
      const result = await this.insertOrIgnoreMessage(msg);
      if (result.inserted) {
        insertedCount += 1;
      }
    }
    return insertedCount;
  }
  
  async upsertSession(session: DBSession): Promise<number> {
    const existing = await this.sessions.where('sessionKey').equals(session.sessionKey).first();
    if (existing && existing.id) {
      await this.sessions.update(existing.id, session);
      return existing.id;
    } else {
      return await this.sessions.add(session);
    }
  }
  
  async getAllSessions(): Promise<DBSession[]> {
    return await this.sessions.orderBy('updatedTime').reverse().toArray();
  }
  
  async getSession(sessionKey: string): Promise<DBSession | undefined> {
    return await this.sessions.where('sessionKey').equals(sessionKey).first();
  }

  async upsertUser(user: DBUser): Promise<void> {
    await this.users.put(user);
  }

  async getAllUsers(): Promise<DBUser[]> {
    return await this.users.toArray();
  }

  async upsertGroup(group: DBGroup): Promise<void> {
    await this.groups.put(group);
  }

  async getAllGroups(): Promise<DBGroup[]> {
    return await this.groups.toArray();
  }
}

let activeDatabase: LocsDatabase | null = null;
let activeOwnerUserId: number | null = null;

/**
 * Open exactly one account-qualified database for the current browser session.
 *
 * Calling this repeatedly for the same account is idempotent. Switching owners
 * closes the prior Dexie connection before exposing the new database so no
 * asynchronous caller can accidentally continue against another account's
 * local namespace.
 */
export async function openAccountDatabase(ownerUserId: number): Promise<LocsDatabase> {
  if (activeDatabase && activeOwnerUserId === ownerUserId) {
    if (!activeDatabase.isOpen()) await activeDatabase.open();
    return activeDatabase;
  }
  activeDatabase?.close();
  const next = new LocsDatabase(ownerUserId);
  await next.open();
  activeDatabase = next;
  activeOwnerUserId = ownerUserId;
  return next;
}

export function requireAccountDatabase(): LocsDatabase {
  if (!activeDatabase || !activeDatabase.isOpen()) {
    throw new Error('The account-scoped local database has not been opened');
  }
  return activeDatabase;
}

export function closeAccountDatabase(): void {
  activeDatabase?.close();
  activeDatabase = null;
  activeOwnerUserId = null;
}
